import IBaseRespone from '../../model/respone/IBaseRespone';
import httpClient from '../httpService';
import {
    BSKT_Diem_ViewModel,
    BSKT_NhapDiem_Input,
    ImportQuaTrinhHocRequest,
    ImportQuaTrinhHocResponse,
    BSKT_TongHopDiemSinhVien,
    BSKT_TongHopDiemTheoNamSinhVien
} from './DiemModels';
export type UnlockReqStatus = 'pending' | 'approved' | 'rejected';

const DiemApiUrl = '/api/diem/';

export interface DiemLockStatusResponse {
    is_locked: boolean;
    pending_unlock_request: boolean;
    ly_do_pending?: string | null;
    created_at_pending?: string | null;
}

export interface PendingUnlockRequestItem {
    id: number;
    id_lop_bskt: number;

    ky_hieu_lop?: string | null;
    ten_lop?: string | null;
    ten_mon?: string | null;

    so_thi_sinh?: number | null;
    requested_by?: string | null;
    requested_at?: string | null;
    requested_reason?: string | null;
    student_details?: string | null;
}
export interface UnlockRequestItem {
    id: number;                 // ID_request
    id_lop_bskt: number;

    ky_hieu_lop?: string | null;
    ten_lop?: string | null;
    ten_mon?: string | null;

    so_thi_sinh?: number | null;

    requested_by?: string | null;
    requested_at?: string | null;
    requested_reason?: string | null;
    student_details?: string | null;

    status: UnlockReqStatus;

    approved_by?: string | null;
    approved_at?: string | null;

    rejected_by?: string | null;
    rejected_at?: string | null;
    rejected_reason?: string | null;
}
const normalizeStatus = (v: any): UnlockReqStatus => {
    // BE có thể trả: 0/1/2 hoặc "pending"/"approved"/"rejected" hoặc "0"
    if (v === 0 || v === '0' || v === 'pending') return 'pending';
    if (v === 1 || v === '1' || v === 'approved') return 'approved';
    if (v === 2 || v === '2' || v === 'rejected') return 'rejected';
    return 'pending';
};

const normalizeUnlockItem = (x: any): UnlockRequestItem => {
    return {
        id: Number(x?.id ?? x?.ID_request ?? x?.ID ?? 0),

        // ✅ nếu SP trả ID_lop_bskt
        id_lop_bskt: Number(x?.id_lop_bskt ?? x?.ID_lop_bskt ?? 0) || 0,

        ky_hieu_lop: x?.ky_hieu_lop ?? x?.Ky_hieu_lop ?? x?.Ky_hieu ?? null,
        ten_lop: x?.ten_lop ?? x?.Ten_lop ?? x?.Ten_lop_bskt ?? null,
        ten_mon: x?.ten_mon ?? x?.Ten_mon ?? null,

        // ✅ QUAN TRỌNG: Pending SP đang có Total_items
        so_thi_sinh: Number(
            x?.so_thi_sinh ??
            x?.So_thi_sinh ??
            x?.Total_items ??     // ✅ thêm dòng này
            x?.total_items ??     // ✅ phòng trường hợp camelCase
            x?.so_sv ??
            0
        ) || 0,

        requested_by:
            x?.requested_by ??
            x?.Nguoi_gui ??
            x?.nguoi_gui ??
            x?.Nguoi_tao ??
            x?.nguoi_tao ??
            null,
        requested_at: x?.requested_at ?? x?.Created_at ?? x?.created_at ?? null,
        requested_reason: x?.requested_reason ?? x?.Ly_do ?? x?.ly_do ?? null,
        student_details:
            x?.student_details ??
            x?.Danh_sach_sinh_vien ??
            x?.danh_sach_sinh_vien ??
            null,

        status: normalizeStatus(x?.status ?? x?.Status),

        approved_by:
            x?.approved_by ??
            x?.Nguoi_duyet ??
            x?.nguoi_duyet ??
            null,
        approved_at: x?.approved_at ?? x?.Approved_at ?? x?.last_modified_times ?? x?.Last_modified_times ?? null,

        rejected_by:
            x?.rejected_by ??
            x?.Nguoi_tu_choi ??
            x?.nguoi_tu_choi ??
            null,
        rejected_at: x?.rejected_at ?? x?.Rejected_at ?? x?.last_modified_times ?? x?.Last_modified_times ?? null,
        rejected_reason: x?.rejected_reason ?? x?.Reject_reason ?? x?.reject_reason ?? null,
    };
};

const unwrapResponse = <T>(payload: any): IBaseRespone<T> => {
    if (payload && typeof payload === 'object' && 'is_success' in payload) {
        return payload as IBaseRespone<T>;
    }

    if (payload?.data && typeof payload.data === 'object' && 'is_success' in payload.data) {
        return payload.data as IBaseRespone<T>;
    }

    return {
        is_success: false,
        message: 'Không đọc được phản hồi từ máy chủ',
        data: null as any
    } as IBaseRespone<T>;
};

export const DiemService = {
    getDiemByLop: async (lopId: number): Promise<BSKT_Diem_ViewModel[]> => {
        const res = await httpClient.get(DiemApiUrl + 'selectByLop/' + lopId);
        if (res.data?.is_success === false || !Array.isArray(res.data?.data)) {
            throw new Error(res.data?.message || 'Không tải được danh sách điểm theo lớp.');
        }
        return res.data.data;
    },

    nhapDiem: async (body: BSKT_NhapDiem_Input): Promise<any> => {
        const res = await httpClient.post(DiemApiUrl + 'nhapDiem', body);
        return res.data.data;
    },

    tongHopKetQuaHoc: async (idDotTuyen: number, idChuyenNganh: number): Promise<any> => {
        const res = await httpClient.get(DiemApiUrl + 'tongHopKetQuaHoc/' + idDotTuyen + '/' + idChuyenNganh);
        return res.data.data;
    },

    importQuaTrinhHocValidate: async (
        body: ImportQuaTrinhHocRequest
    ): Promise<IBaseRespone<ImportQuaTrinhHocResponse>> => {
        const res = await httpClient.post(DiemApiUrl + 'ImportQuaTrinhHocValidate', body);
        return unwrapResponse<ImportQuaTrinhHocResponse>(res);
    },

    importQuaTrinhHocImportValid: async (
        body: ImportQuaTrinhHocRequest
    ): Promise<IBaseRespone<ImportQuaTrinhHocResponse>> => {
        const res = await httpClient.post(DiemApiUrl + 'ImportQuaTrinhHocImportValid', body);
        return unwrapResponse<ImportQuaTrinhHocResponse>(res);
    },

    tongHopDiem: async (
        idDotTuyen: number,
        idChuyenNganh: number
    ): Promise<IBaseRespone<BSKT_TongHopDiemSinhVien[]>> => {
        const res = await httpClient.get(DiemApiUrl + 'TongHopDiem', {
            params: {
                ID_dot_tuyen: idDotTuyen,
                ID_chuyen_nganh: idChuyenNganh
            }
        });
        return res.data;
    },

    tongHopDiemTheoNam: async (
        namTuyenSinh: number
    ): Promise<IBaseRespone<BSKT_TongHopDiemTheoNamSinhVien[]>> => {
        const res = await httpClient.get(DiemApiUrl + 'TongHopDiemTheoNam', {
            params: {
                Nam_tuyen_sinh: namTuyenSinh
            }
        });
        return res.data;
    },

    chotDiem: async (idDotTuyen: number, idChuyenNganh: number): Promise<IBaseRespone<BSKT_TongHopDiemSinhVien[]>> => {
        const res = await httpClient.post(DiemApiUrl + 'ChotDiem', {
            ID_dot_tuyen: idDotTuyen,
            ID_chuyen_nganh: idChuyenNganh
        });
        return res.data;
    },

    chotDiemTheoNam: async (
        namTuyenSinh: number,
        idNhomNganhGanDung: number,
        soMonBskt: number,
        quyetDinh: string,
        ghiChu: string,
        idThiSinhs?: number[]
    ): Promise<IBaseRespone<boolean>> => {
        const res = await httpClient.post(DiemApiUrl + 'ChotDiem', {
            Nam_tuyen_sinh: namTuyenSinh,
            ID_nhom_nganh_gan_dung: idNhomNganhGanDung,
            So_mon_bskt: soMonBskt,
            Quyet_dinh: quyetDinh || '',
            Ghi_chu: ghiChu || '',
            ID_thi_sinhs: idThiSinhs || []
        });
        // HTTP failures are flattened by httpService instead of rejected.
        const response = (res as any)?.is_success === false ? res as any : res?.data;
        if (response?.is_success !== true || response?.data !== true) {
            throw new Error(response?.message || 'Chưa xác nhận được kết quả chốt điểm. Vui lòng tải lại dữ liệu để kiểm tra.');
        }
        return response;
    },

    getAll: async (): Promise<any[]> => {
        const res = await httpClient.get(DiemApiUrl + 'getAll');
        return res.data.data;
    },

    getById: async (id: number): Promise<any> => {
        const res = await httpClient.get(DiemApiUrl + 'getById/' + id);
        return res.data.data;
    },

    create: async (body: any): Promise<any> => {
        const res = await httpClient.post(DiemApiUrl + 'insert', body);
        return res.data.data;
    },

    update: async (id: number, body: any): Promise<any> => {
        const res = await httpClient.put(DiemApiUrl + 'update/' + id, body);
        return res.data.data;
    },

    delete: async (id: number): Promise<boolean> => {
        const res = await httpClient.delete(DiemApiUrl + 'delete/' + id);
        return res.data.data;
    },

    // ✅ Xuất PDF
    exportPDF: async (idLop: number): Promise<void> => {
        const res = await httpClient.get(DiemApiUrl + 'InBangDiem?ID_lop_bskt=' + idLop, {
            responseType: 'blob'
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'BangDiem.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
    },

    // ✅ Xuất Excel
    exportExcel: async (idLop: number): Promise<void> => {
        const res = await httpClient.get(DiemApiUrl + 'ExportExcel?ID_lop_bskt=' + idLop, {
            responseType: 'blob'
        });

        const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'BangDiem.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    },

    // ===================== 🔐 LOCK / UNLOCK (THEO LỚP) =====================
    // Backend bạn đang có:
    // GET  /api/diem/LockStatusLop?ID_lop_bskt=xx
    // POST /api/diem/LockDiemLop      { ID_lop_bskt }
    // POST /api/diem/UnlockDiemLop    { ID_lop_bskt }

    getLockStatus: async (idLop: number): Promise<DiemLockStatusResponse> => {
        const res = await httpClient.get(DiemApiUrl + 'LockStatusLop', {
            params: { ID_lop_bskt: idLop }
        });

        // backend trả: { lop_is_locked, locked_at, locked_by }
        // nhưng page đang dùng: { is_locked, pending_unlock_request... }
        // => map cho đúng field đang dùng
        const data = res.data.data;
        return {
            is_locked: !!(data?.lop_is_locked ?? false),
            pending_unlock_request: false,
            ly_do_pending: null,
            created_at_pending: null
        };
    },

    lockDiem: async (idLop: number): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'LockDiemLop', { ID_lop_bskt: idLop });
        return !!res.data.data;
    },

    unlockDiem: async (idLop: number): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'UnlockDiemLop', { ID_lop_bskt: idLop });
        return !!res.data.data;
    },

    // ===================== 🔐 LOCK / UNLOCK (CÁ NHÂN) =====================
    // Backend bạn đang có:
    // POST /api/diem/LockDiemCaNhan     { ID_diem }
    // POST /api/diem/UnlockDiemCaNhan   { ID_diem }

    lockDiemCaNhan: async (idDiem: number): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'LockDiemCaNhan', { ID_diem: idDiem });
        return !!res.data.data;
    },

    unlockDiemCaNhan: async (idDiem: number): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'UnlockDiemCaNhan', { ID_diem: idDiem });
        return !!res.data.data;
    },

    // ===================== (TÙY CHỌN) REQUEST UNLOCK =====================
    // Nếu bạn CHƯA có BE cho RequestUnlock thì để đó không dùng cũng được.
    requestUnlock: async (idLop: number, lyDo: string): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'RequestUnlock', {
            ID_lop_bskt: idLop,
            Ly_do: lyDo
        });
        return !!res.data.data;
    },
    getPendingUnlockRequests: async (): Promise<PendingUnlockRequestItem[]> => {
        const res = await httpClient.get(DiemApiUrl + 'UnlockRequestsPending');
        return res.data.data ?? [];
    },
    // ===================== 🔔 UNLOCK REQUEST (TRUNG TÂM DUYỆT) =====================

    // List theo trạng thái + mine
    getUnlockRequests: async (status?: UnlockReqStatus, mine?: boolean): Promise<UnlockRequestItem[]> => {
        const res = await httpClient.get(DiemApiUrl + 'UnlockRequests', { params: { status, mine } });
        if (res.data?.is_success === false || !Array.isArray(res.data?.data)) {
            throw new Error(res.data?.message || 'Không tải được yêu cầu mở khóa.');
        }
        const arr = res.data.data;
        return arr.map(normalizeUnlockItem);
    },

    // tạo đề xuất mở khóa (từ trang nhập điểm)
    createUnlockRequest: async (
        idDiems: number[],
        lyDo: string,
        ctx?: { ID_lop_bskt?: number; ID_mon?: number }
    ): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'UnlockRequestCreate', {
            ID_diems: idDiems,
            Ly_do: lyDo,
            // ✅ gửi kèm để BE lưu và trả tên lớp/môn
            ...(ctx?.ID_lop_bskt ? { ID_lop_bskt: ctx.ID_lop_bskt } : {}),
            ...(ctx?.ID_mon ? { ID_mon: ctx.ID_mon } : {})
        });
        return !!res.data.data;
    },

    // duyệt
    approveUnlockRequest: async (idRequest: number): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'UnlockRequestApprove', {
            ID_request: idRequest
        });
        if (res.data?.is_success === false) throw new Error(res.data?.message || 'Duyệt yêu cầu không thành công.');
        return res.data?.data === true;
    },

    // từ chối
    rejectUnlockRequest: async (idRequest: number, reason: string): Promise<boolean> => {
        const res = await httpClient.post(DiemApiUrl + 'UnlockRequestReject', {
            ID_request: idRequest,
            Reject_reason: reason
        });
        if (res.data?.is_success === false) throw new Error(res.data?.message || 'Từ chối yêu cầu không thành công.');
        return res.data?.data === true;
    },
};
